import { useEffect, useRef } from 'react';

/**
 * Plays a looping ringtone while `active` is true, using the Web Audio API so we
 * don't need to ship an audio asset. A classic two-tone "double ring" cadence:
 * two short bursts, then a pause, repeating.
 *
 * Requires a prior user gesture (login click) to satisfy autoplay policies —
 * which the agent always has by the time a call comes in.
 */
export function useRingtone(active: boolean) {
  const ctxRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) return;

    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;

    const ctx = new AudioCtx();
    ctxRef.current = ctx;
    void ctx.resume().catch(() => {});

    // One ring burst: a 440Hz+480Hz blend (North-American-ish ringback feel).
    const burst = (start: number, dur: number) => {
      for (const freq of [440, 480]) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.12, start + 0.02);
        gain.gain.setValueAtTime(0.12, start + dur - 0.04);
        gain.gain.linearRampToValueAtTime(0, start + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + dur);
      }
    };

    // Cadence: ring 0.4s, gap 0.2s, ring 0.4s, then ~2s silence — every 3s.
    const schedule = () => {
      const t = ctx.currentTime;
      burst(t + 0.05, 0.4);
      burst(t + 0.65, 0.4);
    };
    schedule();
    timerRef.current = window.setInterval(schedule, 3000);

    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
      ctx.close().catch(() => {});
      ctxRef.current = null;
    };
  }, [active]);
}
